import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/core/base.component';
import { ScheduledBookingService } from '../services/scheduled-booking.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { SessionService } from 'src/app/shared/services/session.service';
import { ScheduledBooking } from '../models/scheduled-booking';
import { Sort } from '@angular/material/sort';
import { ScheduledBookingTypeEnum, ScheduledBookingTypeEnumLabel } from 'src/app/shared/models/scheduled-booking-type.enum';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateScheduledBookingModalComponent } from '../modals/booking/create-scheduled-booking-modal/create-scheduled-booking-modal.component';
import { EditScheduledBookingModalComponent } from '../modals/booking/edit-scheduled-booking-modal/edit-scheduled-booking-modal.component';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { ScheduledBookingListQueryFilter } from './scheduled-booking-list-query-filter';
import { SimulatorService } from '../services/simulator.service';
import { Simulator } from '../models/simulators';
import * as moment from 'moment-timezone';
import { environment } from 'src/environments/environment';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { Subscription } from 'rxjs';


@Component({
    selector: 'scheduled-booking-list',
    templateUrl: './scheduled-booking-list.component.html',
    styleUrl: './scheduled-booking-list.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class ScheduledBookingListComponent extends BaseComponent implements OnInit, OnDestroy {
    public scheduledBookings: ScheduledBooking[] = [];
    @Input() filter: ScheduledBookingListQueryFilter = new ScheduledBookingListQueryFilter();
    public updateFilterEvent = new EventEmitter<any>();
    public displayedColumns: string[] = ['name', 'type', 'hasPaidBooking', 'startDate', 'endDate', 'timeSlot', 'price', 'simulators', 'actions'];
    scheduledBookingTypes: any[] = ScheduledBookingTypeEnumLabel;
    userTimezone: string;
    simulators: Simulator[] = [];

    private scheduledBookingUpdateSubscription: Subscription;

    constructor(sessionService: SessionService,
        stringHelpers: StringHelpers,
        private matDialog: MatDialog,
        private scheduledBookingService: ScheduledBookingService,
        private simulatorService: SimulatorService,
        private signalrService: SignalrService
    ) {
        super(sessionService, stringHelpers);
        this.initializeSignalR();
    }

    ngOnInit(): void {
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.userTimezone = this.sessionService.getUser().timeZone;
        this.filter.timeZone = this.userTimezone;
        this.getSimulators();
    }


    ngOnDestroy(): void {
        if (this.scheduledBookingUpdateSubscription) {
            this.scheduledBookingUpdateSubscription.unsubscribe();
        }
    }

    private initializeSignalR(): void {
        if (this.sessionService.isUserLoggedIn()) {
          this.signalrService.startConnection(environment.signalR.bookingHubUrl, this.sessionService.getUser().auth0Id);
          this.waitForConnectionAndSetupListeners();
        }
      }
    
      private waitForConnectionAndSetupListeners(): void {
        const checkConnection = () => {
          if (this.signalrService.isConnected()) {
            this.setupBookingListeners();
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        
        setTimeout(checkConnection, 100);
      }
    
      private setupBookingListeners(): void {

        this.scheduledBookingUpdateSubscription = this.signalrService.getListnerConnection(environment.signalR.scheduledBookingPaymentUpdateListener)
          .subscribe((result: ScheduledBooking) => {
            this.scheduledBookings.find(scheduledBooking => scheduledBooking.id === result.id).hasPaidBooking = true;
          });
    
      }

    getScheduledBookings(): void {
        this.isLoading = true;

        if (this.filter.fromDate) {
            // Set to start of day in user's timezone
            this.filter.fromDate = moment.tz(this.filter.fromDate, this.userTimezone).startOf('day').toDate();
        }

        if (this.filter.toDate) {
            // Set to end of day in user's timezone to capture all events on that date
            this.filter.toDate = moment.tz(this.filter.toDate, this.userTimezone).endOf('day').toDate();
        }

        this.scheduledBookingService.getAllByOrganizationId(this.filter).subscribe({
            next: (result: any) => {
                this.scheduledBookings = result.results;
                this.filter.totalCount = result.totalCount;
                this.updateFilterEvent.emit(result.results);
                this.isLoading = false;
            },
            error: (error: any) => {
                this.alert(error);
                console.error(error);
                this.isLoading = false;
            }
        });
    }

    getSimulators(): void {
        this.simulatorService.getAllByOrganization({ organizationId: this.sessionService.getUser().organizationId }).subscribe({
            next: (result: any) => {
                this.simulators = result.results;
                this.getScheduledBookings();
            },
            error: (error: any) => {
                console.error(error);
            }
        });
    }
    
    resetFilter(): void {
        this.filter = new ScheduledBookingListQueryFilter();
        this.filter.organizationId = this.sessionService.getUser().organizationId;
        this.filter.timeZone = this.userTimezone;
        this.filter.fromDate = null;
        this.filter.toDate = null;
        this.filter.scheduledBookingType = null;
        this.filter.page = 1;
        this.filter.pageSize = 10;
    }

    sortData(sort: Sort): void {
        const data = this.scheduledBookings.slice();
        if (!sort.active || sort.direction === '') {
            this.scheduledBookings = data;
            return;
        }

        this.scheduledBookings = data.sort((a, b) => {
            const isAsc = sort.direction === 'asc';
            switch (sort.active) {
                case 'name':
                    return this.compare(a.name, b.name, isAsc);
                case 'type':
                    return this.compare(a.type, b.type, isAsc);
                case 'startDate':
                    return this.compare(a.startTime, b.startTime, isAsc);
                case 'endDate':
                    return this.compare(a.endTime, b.endTime, isAsc);
                case 'price':
                    return this.compare(a.price, b.price, isAsc);
                default:
                    return 0;
            }
        });
    }

    compare(a: number | string | Date, b: number | string | Date, isAsc: boolean): number {
        if (a instanceof Date && b instanceof Date) {
            return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
        }
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    onCreate(): void {
        const recurrenceData: ScheduledBooking = {
            id: '',
            organizationId: this.sessionService.getUser().organizationId,
            lastEditedBy: this.sessionService.getUserId(),
            startTime: new Date(),
            endTime: new Date(new Date().getTime() + (60 * 60 * 1000)),
            simulatorIds: [],
            type: null,
            name: '',
            price: null,
        };
        const dialogRef = this.matDialog.open(CreateScheduledBookingModalComponent, {
            width: '80vw',
            height: '85vh',
            disableClose: true,
            data: recurrenceData
        });

        const simulators = [];
        this.simulators.forEach(simulator => {
            if (simulator.isAvailable === true) {
                simulators.push({
                    id: simulator.id,
                    simulatorTitle: simulator.bayName,
                });
            }
        });

        dialogRef.componentInstance.simulators = simulators;

        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.action === 'save') {
                this.getScheduledBookings();
            }
        });
    }

    onEdit(scheduledBooking: ScheduledBooking): void {
        const dialogRef = this.matDialog.open(EditScheduledBookingModalComponent, {
            width: '80vw',
            height: '85vh',
            disableClose: true,
        });


        const simulators = [];
        this.simulators.forEach(simulator => {
            if (simulator.isAvailable === true) {
                simulators.push({
                    id: simulator.id,
                    simulatorTitle: simulator.bayName,
                });
            }
        });

        dialogRef.componentInstance.id = scheduledBooking.id;
        dialogRef.componentInstance.scheduledBooking = scheduledBooking;
        dialogRef.componentInstance.simulators = simulators;

        dialogRef.afterClosed().subscribe((result) => {
            if (result && (result.action === 'save' || result.action === 'delete')) {
                this.getScheduledBookings();
            }
        });
    }

    onDelete(scheduledBooking: ScheduledBooking): void {
        const dialogRef = this.matDialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Delete Scheduled Booking';
        dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> 
        Are you sure you want to delete Scheduled Booking <strong>${scheduledBooking.name}</strong>? 
        <br /><br /> If this was a recurring booking, all future bookings will also be deleted but previous bookings will remain in the scheduler.`;

        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.isLoading = true;
                this.scheduledBookingService.delete({ 
                    id: scheduledBooking.id, 
                    lastEditedBy: this.sessionService.getUserId(), 
                    timezone: this.userTimezone,
                    organizationId: this.sessionService.getUser().organizationId,
                }).subscribe({
                    next: (result) => {
                        this.getScheduledBookings();
                        this.isLoading = false;
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    onResendPaymentLink(scheduledBooking: ScheduledBooking): void {
        console.log(scheduledBooking);
        const dialogRef = this.matDialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Resend Payment Link';
        dialogRef.componentInstance.body = 'Are you sure you want to resend the payment link to the customer?';
        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;
        dialogRef.componentInstance.thirdButtonText = 'Regenerate Payment Link';
        dialogRef.componentInstance.isThridButtonDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                let dto = {
                    successUrl: `${environment.dashboardLocation}/payment-success-failure`, 
                    scheduledBooking: scheduledBooking,
                    regeneratePaymentLink: false
                };
                this.scheduledBookingService.resendPaymentLink(dto).subscribe({
                    next: (result) => {
                        this.getScheduledBookings();
                    }
                });
            }

            if(response === 'Regenerate Payment Link') {
                let dto = {
                    successUrl: `${environment.dashboardLocation}/payment-success-failure`, 
                    scheduledBooking: scheduledBooking,
                    regeneratePaymentLink: true
                };
                this.scheduledBookingService.resendPaymentLink(dto).subscribe({
                    next: (result) => {
                        this.getScheduledBookings();
                    }
                });
            }
        });
    }

    getScheduledBookingTypeLabel(type: any): string {
        const typeObj = this.scheduledBookingTypes.find(t => t.type === type);
        return typeObj ? typeObj.name : type;
    }

    getSimulatorNames(simulatorIds: string[]): string {
        return this.simulators.filter(simulator => simulatorIds.includes(simulator.id)).map(simulator => simulator.bayName).join(', ');
    }

    formatInUserTimezone(date: Date, format: string): string {
        return moment.utc(date).tz(this.userTimezone).format(format);
    }

    isEndDatePassed(scheduledBooking: ScheduledBooking): boolean {
        if (!scheduledBooking.endTime) {
            return false;
        }
        const endDate = moment.utc(scheduledBooking.endTime).tz(this.userTimezone);
        const now = moment.tz(this.userTimezone);
        return endDate.isBefore(now, 'day');
    }

    canResendPaymentLink(scheduledBooking: ScheduledBooking): boolean {
        if(this.isEndDatePassed(scheduledBooking)){
            return false;
        }

        return scheduledBooking.type !== ScheduledBookingTypeEnum.League && !scheduledBooking.hasPaidBooking;
    }

    alert(error: any) {
        const dialogRef = this.matDialog.open(AlertModalComponent, {
            width: '35vw',
        });

        let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

        if (error.status === 400) {
            error.error.forEach((error: any) => {
                errorMessage += `<li>${error.errorMessage}</li>`;
            });
        } else {
            errorMessage += `<li>There was an error loading the scheduled bookings, Please try again or contact support if the problem persists</li>`;
        }

        dialogRef.componentInstance.title = 'Scheduled Bookings Error';
        dialogRef.componentInstance.body = errorMessage + '</ul>';
        dialogRef.componentInstance.submitText = 'OK';
    }
}
